import { HubSearchResult, SearchParams } from '@api/models';

import { ApiClient } from '@website/lib/api';
import { getAssetUrl } from '@website/utils/assets';

import { type GetActivitiesResult, toActivitySearchResults } from './activities.service';

const HUB_SELECTION_PREFIX = 'hub:';

/**
 * The id a Hub has among the search page's selectable results (cards, pins, the drawer), beside the
 * activities' own ids.
 */
export const hubSelectionId = (hubId: string): string => `${HUB_SELECTION_PREFIX}${hubId}`;

/** The Hub's id when `selectionId` names a Hub, else null (an activity). */
export const hubIdFromSelection = (selectionId: string | null | undefined): string | null =>
  selectionId?.startsWith(HUB_SELECTION_PREFIX) ? selectionId.slice(HUB_SELECTION_PREFIX.length) : null;

/** A Hub the search found: its card in the list, its pin on the map. */
export interface SearchHub {
  id: string;
  name: string;
  /** "34 Boulevard du Port" */
  street: string;
  /** "95000 Cergy" */
  locality: string;
  city: string;
  coverImageUrl: string | null;
  coordinates: { latitude: number; longitude: number };
}

/** The search page's results, one section per kind. */
export interface SearchResult {
  activities: GetActivitiesResult;
  hubs: SearchHub[];
}

function toSearchHub(hub: HubSearchResult): SearchHub {
  return {
    id: hub.id,
    name: hub.name,
    street: hub.addressLine1,
    locality: [hub.zipCode, hub.city].filter(Boolean).join(' '),
    city: hub.city,
    coverImageUrl: hub.coverImageKey ? getAssetUrl(hub.coverImageKey) : null,
    coordinates: { latitude: hub.latitude, longitude: hub.longitude }
  };
}

/** Everything the search page shows for its filters, in one request (`GET /search`). */
export async function search(params: SearchParams, locale: string): Promise<SearchResult> {
  // Signed in, the caller's groups widen what it may see: the token goes along, as on /activities.
  const response = await ApiClient.search(params);
  return {
    activities: toActivitySearchResults(response.activities, locale),
    hubs: response.hubs.map(toSearchHub)
  };
}
